import { Injectable } from '@angular/core';
import { PagedResultDto } from '../../models/table.model';
import {
  ParkingFinanceListRowDto,
  ParkingFinanceListRow,
  ParkingFinanceMarketingDashboardDto,
  ParkingFinanceMarketingDashboardView,
  ParkingFinanceSummaryDto,
  ParkingFinanceDashboardStat,
  CancelParkingFinanceResponseDto,
  ParkingFinanceRecentActivityDto,
  ParkingFinanceChartItemDto,
  ParkingFinanceFilterOptionsDto,
  IssueFullTaxInvoiceFromFinancePayload,
  IssueFullTaxInvoiceFromFinanceResponseDto,
  IssueFullTaxInvoiceFromFinanceResult,
  IssueFullTaxFromFinanceForm
} from '../../models/finance/parking-finance.model';

@Injectable({ providedIn: 'root' })
export class MapperParkingFinanceService {
  // ================= Normal Functions =================

  /** Maps paginated DTO results to UI rows */
  mapTableFromDto(dto: PagedResultDto<ParkingFinanceListRowDto>): {
    items: ParkingFinanceListRow[];
    total: number;
  } {
    const raw = dto as any;
    const data = dto.data ?? raw['Data'] ?? raw['data'] ?? [];
    return {
      items: data.map((item: any) => this.normalizeRow(item)),
      total: Number(dto.total ?? raw['Total'] ?? raw['total'] ?? 0),
    };
  }

  /** Maps dashboard summary, activities, and options to UI dashboard view */
  mapMarketingDashboard(dto: ParkingFinanceMarketingDashboardDto): ParkingFinanceMarketingDashboardView {
    const raw = dto as any;
    const summary = this.normalizeSummary(dto.summary ?? raw['Summary'] ?? raw['summary']);
    const recentActivityRaw = dto.recentActivity ?? raw['RecentActivity'] ?? raw['recentActivity'] ?? [];
    const chartItemsRaw = dto.chartItems ?? raw['ChartItems'] ?? raw['chartItems'] ?? [];
    const filterOptionsRaw = dto.filterOptions ?? raw['FilterOptions'] ?? raw['filterOptions'] ?? { feeTypes: [], feeNames: [], statuses: [] };

    return {
      stats: this.mapDashboardStats(summary),
      recentActivity: recentActivityRaw.map((item: any) => this.normalizeRecent(item)),
      chartItems: chartItemsRaw.map((item: any) => this.normalizeChartItem(item)),
      filterOptions: this.normalizeFilterOptions(filterOptionsRaw),
    };
  }

  /** Normalizes search query fields to lowercase exact strings matching the API */
  mapSearchField(type: 'all' | 'receiptid' | 'feename' | 'feetypename' | 'sitename' | 'status'): string {
    return type;
  }

  /** Build default form values for issuing full tax invoice from finance */
  mapIssueFullTaxFormFromRecord(record: ParkingFinanceListRow): IssueFullTaxFromFinanceForm {
    const today = new Date();
    const createBill = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const details = [record.feeName, record.siteName].filter(Boolean).join(' - ');

    return {
      customerName: '',
      taxId: '',
      address: '',
      details: details || record.feeName || '',
      reference: record.receiptId || '',
      createBill,
      remark: '',
      cardName: '',
      type: '',
      telephone: '',
      email: '',
      postcode: '',
      headquarters: true,
      branchNo: '',
      branchName: '',
    };
  }

  /** Map form + context to API payload */
  mapIssueFullTaxPayload(
    form: IssueFullTaxFromFinanceForm,
    userId: number,
    menuId: number,
    siteId: number,
    financeId: number
  ): IssueFullTaxInvoiceFromFinancePayload {
    const payload: IssueFullTaxInvoiceFromFinancePayload = {
      userId,
      menuId,
      siteId,
      financeId,
      customerName: form.customerName.trim(),
      address: form.address.trim(),
      taxId: form.taxId.trim(),
      details: form.details.trim(),
      headquarters: form.headquarters,
    };

    if (form.createBill) payload.createBill = form.createBill;
    if (form.reference.trim()) payload.reference = form.reference.trim();
    if (form.remark.trim()) payload.remark = form.remark.trim();
    if (form.cardName.trim()) payload.cardName = form.cardName.trim();
    if (form.type.trim()) payload.type = form.type.trim();
    if (form.telephone.trim()) payload.telephone = form.telephone.trim();
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.postcode.trim()) payload.postcode = form.postcode.trim();
    if (!form.headquarters && form.branchNo.trim()) payload.branchNo = form.branchNo.trim();
    if (!form.headquarters && form.branchName.trim()) payload.branchName = form.branchName.trim();

    return payload;
  }

  /** Safe validation of issue full tax response fields */
  mapIssueFullTaxResponse(res: IssueFullTaxInvoiceFromFinanceResponseDto): IssueFullTaxInvoiceFromFinanceResult {
    const r = res as any;
    return {
      isSuccess: Boolean(r.isSuccess ?? r['IsSuccess'] ?? false),
      message: String(r.message ?? r['Message'] ?? ''),
      messageEn: String(r.messageEn ?? r['MessageEn'] ?? ''),
      oldFinanceId: Number(r.oldFinanceId ?? r['OldFinanceId'] ?? 0),
      oldReceiptId: String(r.oldReceiptId ?? r['OldReceiptId'] ?? ''),
      newFinanceId: Number(r.newFinanceId ?? r['NewFinanceId'] ?? 0),
      newReceiptId: String(r.newReceiptId ?? r['NewReceiptId'] ?? ''),
      fullTaxInvoiceId: Number(r.fullTaxInvoiceId ?? r['FullTaxInvoiceId'] ?? 0),
    };
  }

  /** Safe validation of cancel response fields */
  mapCancelResponse(res: CancelParkingFinanceResponseDto): CancelParkingFinanceResponseDto {
    const r = res as any;
    return {
      isSuccess: Boolean(r.isSuccess ?? r['IsSuccess'] ?? r['isSuccess'] ?? false),
      message: String(r.message ?? r['Message'] ?? r['message'] ?? ''),
      messageEn: String(r.messageEn ?? r['MessageEn'] ?? r['messageEn'] ?? ''),
      requiresApproval: Boolean(r.requiresApproval ?? r['RequiresApproval'] ?? r['requiresApproval'] ?? false),
      approvalRequestId: Number(r.approvalRequestId ?? r['ApprovalRequestId'] ?? r['approvalRequestId'] ?? 0),
      financeId: Number(r.financeId ?? r['FinanceId'] ?? r['financeId'] ?? 0),
    };
  }

  /** Safe row normalization to avoid crashes on undefined properties */
  private normalizeRow(item: any): ParkingFinanceListRow {
    const r = item || {};
    return {
      id: Number(r.id ?? r['Id'] ?? r['id'] ?? 0),
      receiptId: String(r.receiptId ?? r['ReceiptId'] ?? r['receiptId'] ?? ''),
      paidDate: r.paidDate ?? r['PaidDate'] ?? r['paidDate'] ?? null,
      fee: Number(r.fee ?? r['Fee'] ?? r['fee'] ?? 0),
      vat: Number(r.vat ?? r['Vat'] ?? r['vat'] ?? 0),
      wh: Number(r.wh ?? r['Wh'] ?? r['wh'] ?? 0),
      total: Number(r.total ?? r['Total'] ?? r['total'] ?? 0),
      statusId: String(r.statusId ?? r['StatusId'] ?? r['statusId'] ?? ''),
      status: String(r.status ?? r['Status'] ?? r['status'] ?? ''),
      feeId: Number(r.feeId ?? r['FeeId'] ?? r['feeId'] ?? 0),
      feeName: String(r.feeName ?? r['FeeName'] ?? r['feeName'] ?? ''),
      feeTypeId: Number(r.feeTypeId ?? r['FeeTypeId'] ?? r['feeTypeId'] ?? 0),
      feeTypeName: String(r.feeTypeName ?? r['FeeTypeName'] ?? r['feeTypeName'] ?? ''),
      siteId: Number(r.siteId ?? r['SiteId'] ?? r['siteId'] ?? 0),
      siteName: String(r.siteName ?? r['SiteName'] ?? r['siteName'] ?? ''),
      canCancel: Boolean(r.canCancel ?? r['CanCancel'] ?? r['canCancel'] ?? false),
      pendingApprovalRequestId: r.pendingApprovalRequestId ?? r['PendingApprovalRequestId'] ?? r['pendingApprovalRequestId'] ?? null,
    };
  }

  /** Safe summary object normalization */
  private normalizeSummary(summary: any): ParkingFinanceSummaryDto {
    const s = summary || {};
    return {
      totalCount: Number(s.totalCount ?? s['TotalCount'] ?? s['totalCount'] ?? 0),
      normalCount: Number(s.normalCount ?? s['NormalCount'] ?? s['normalCount'] ?? 0),
      cancelledCount: Number(s.cancelledCount ?? s['CancelledCount'] ?? s['cancelledCount'] ?? 0),
      newLast7Days: Number(s.newLast7Days ?? s['NewLast7Days'] ?? s['newLast7Days'] ?? 0),
      totalFee: Number(s.totalFee ?? s['TotalFee'] ?? s['totalFee'] ?? 0),
      totalVat: Number(s.totalVat ?? s['TotalVat'] ?? s['totalVat'] ?? 0),
      totalWh: Number(s.totalWh ?? s['TotalWh'] ?? s['totalWh'] ?? 0),
      totalAmount: Number(s.totalAmount ?? s['TotalAmount'] ?? s['totalAmount'] ?? 0),
      avgTransaction: Number(s.avgTransaction ?? s['AvgTransaction'] ?? s['avgTransaction'] ?? 0),
    };
  }

  /** Safe recent activity normalization */
  private normalizeRecent(item: any): ParkingFinanceRecentActivityDto {
    const r = item || {};
    return {
      id: Number(r.id ?? r['Id'] ?? r['id'] ?? 0),
      receiptId: String(r.receiptId ?? r['ReceiptId'] ?? r['receiptId'] ?? ''),
      siteName: String(r.siteName ?? r['SiteName'] ?? r['siteName'] ?? ''),
      feeName: String(r.feeName ?? r['FeeName'] ?? r['feeName'] ?? ''),
      feeTypeName: String(r.feeTypeName ?? r['FeeTypeName'] ?? r['feeTypeName'] ?? ''),
      total: Number(r.total ?? r['Total'] ?? r['total'] ?? 0),
      status: String(r.status ?? r['Status'] ?? r['status'] ?? ''),
      paidDate: String(r.paidDate ?? r['PaidDate'] ?? r['paidDate'] ?? ''),
    };
  }

  /** Safe chart item normalization */
  private normalizeChartItem(item: any): ParkingFinanceChartItemDto {
    const c = item || {};
    const rawFee = Number(c.fee ?? c['Fee'] ?? c['fee'] ?? 0);
    const rawTotal = Number(c.total ?? c['Total'] ?? c['total'] ?? 0);
    const finalVal = rawTotal > 0 ? rawTotal : rawFee;
    return {
      id: Number(c.id ?? c['Id'] ?? c['id'] ?? 0),
      siteName: String(c.siteName ?? c['SiteName'] ?? c['siteName'] ?? ''),
      feeName: String(c.feeName ?? c['FeeName'] ?? c['feeName'] ?? ''),
      paidDate: String(c.paidDate ?? c['PaidDate'] ?? c['paidDate'] ?? ''),
      fee: finalVal,
    };
  }

  /** Safe filter options normalization */
  private normalizeFilterOptions(filterOptions: any): ParkingFinanceFilterOptionsDto {
    const f = filterOptions || {};
    return {
      feeTypes: f.feeTypes ?? f['FeeTypes'] ?? f['feeTypes'] ?? [],
      feeNames: f.feeNames ?? f['FeeNames'] ?? f['feeNames'] ?? [],
      statuses: f.statuses ?? f['Statuses'] ?? f['statuses'] ?? [],
    };
  }

  /** Generates dashboard visual stats configurations */
  private mapDashboardStats(summary: ParkingFinanceSummaryDto): ParkingFinanceDashboardStat[] {
    return [
      {
        title: 'ยอดรวมรับชำระ',
        subtitle: 'รวมยอดทั้งหมด',
        value: (summary.totalAmount || 0).toLocaleString('th-TH') + ' ฿',
        icon: 'payments',
        color: 'text-green-600',
      },
      {
        title: 'จำนวนรายการทั้งหมด',
        subtitle: 'รายการทั้งหมด',
        value: (summary.totalCount || 0).toLocaleString() + ' รายการ',
        icon: 'receipt',
        color: 'text-blue-600',
      },
      {
        title: 'จำนวนรายการยกเลิก',
        subtitle: 'รายการที่ยกเลิกแล้ว',
        value: (summary.cancelledCount || 0).toLocaleString() + ' รายการ',
        icon: 'cancel',
        color: 'text-red-500',
      },
      {
        title: 'เฉลี่ยต่อรายการ',
        subtitle: 'ยอดเฉลี่ย',
        value: (summary.avgTransaction || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ฿',
        icon: 'analytics',
        color: 'text-orange-600',
      },
    ];
  }

  // ================= API Functions =================
  // (None)
}
